import { db } from '../lib/firebase';
import { collection, query, where, orderBy, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import type { Trade } from '../types';

class RunningBalanceService {
  private readonly TRADES_COLLECTION = 'trades';
  private readonly ACCOUNTS_COLLECTION = 'accounts';

  /**
   * Vérifie et initialise automatiquement les running balances si nécessaire
   * Appelé automatiquement lors des calculs pour assurer la compatibilité
   */
  private async ensureRunningBalancesExist(accountId: string, userId: string): Promise<void> {
    try {
      // Vérifier s'il y a des trades sans running balance
      const tradesQuery = query(
        collection(db, this.TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', userId)
      );
      
      const tradesSnapshot = await getDocs(tradesQuery);
      let needsInitialization = false;
      
      // Vérifier si au moins un trade n'a pas de running balance
      tradesSnapshot.forEach(doc => {
        const trade = doc.data() as Trade;
        if (trade.runningBalance === undefined || trade.runningBalance === null) {
          needsInitialization = true;
        }
      });
      
      // Si initialisation nécessaire, le faire automatiquement
      if (needsInitialization && tradesSnapshot.size > 0) {
        // console.log supprimé
        await this.recalculateAllRunningBalances(accountId, userId);
        // console.log supprimé
      }
      
    } catch (error) {
      // console.warn supprimé
      // Ne pas faire échouer l'opération principale
    }
  }

  /**
   * Calcule et met à jour la running balance pour un trade spécifique
   * et propage les changements aux trades suivants
   */
  async updateRunningBalanceFromTrade(
    accountId: string, 
    modifiedTradeId: string, 
    oldTrade: { profitLoss?: number; commission?: number; swap?: number }, 
    newTrade: { profitLoss?: number; commission?: number; swap?: number }
  ): Promise<void> {
    // console.log supprimé
    
    // Calculer l'impact total ancien et nouveau
    const oldTotal = (oldTrade.profitLoss || 0) + (oldTrade.commission || 0) + (oldTrade.swap || 0);
    const newTotal = (newTrade.profitLoss || 0) + (newTrade.commission || 0) + (newTrade.swap || 0);
    const difference = newTotal - oldTotal;
    
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    
    const batch = writeBatch(db);
    
          if (difference === 0) {
        // console.log supprimé
        return;
      }

    try {
      // 1. Récupérer le trade modifié pour avoir sa date
      const modifiedTradeDoc = await getDoc(doc(db, this.TRADES_COLLECTION, modifiedTradeId));
      if (!modifiedTradeDoc.exists()) {
        throw new Error('Trade modifié non trouvé');
      }
      
      const modifiedTrade = modifiedTradeDoc.data() as Trade;
      if (!modifiedTrade.openDateTime) {
        throw new Error('Date d\'ouverture manquante pour le trade modifié');
      }
      
      // 2. Récupérer TOUS les trades de ce compte (filtrage côté client)
      const userId = getAuth().currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');
      const tradesQuery = query(
        collection(db, this.TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', userId)
      );
      
      const tradesSnapshot = await getDocs(tradesQuery);
      const tradesToUpdate: { id: string; ref: any; data: Trade }[] = [];
      
      // Filtrer et trier côté client pour éviter les index composites
      tradesSnapshot.forEach(doc => {
        const trade = { id: doc.id, ...doc.data() } as Trade;
        
        // Filtrer : seulement les trades APRÈS la date du trade modifié
        if (trade.openDateTime && modifiedTrade.openDateTime && trade.openDateTime >= modifiedTrade.openDateTime) {
          // Exclure le trade modifié lui-même (il sera mis à jour séparément)
          if (trade.id !== modifiedTradeId) {
            tradesToUpdate.push({
              id: trade.id,
              ref: doc.ref,
              data: trade
            });
          }
        }
      });
      
      // Trier par date puis par ID pour maintenir l'ordre chronologique
      tradesToUpdate.sort((a, b) => {
        const dateA = a.data.openDateTime || '';
        const dateB = b.data.openDateTime || '';
        if (dateA === dateB) {
          return a.id.localeCompare(b.id);
        }
        return dateA.localeCompare(dateB);
      });
      
      // console.log supprimé
      
      // 3. Mettre à jour la running balance du trade modifié
      const newRunningBalanceForModified = (modifiedTrade.runningBalance || 0) + difference;
      batch.update(doc(db, this.TRADES_COLLECTION, modifiedTradeId), {
        runningBalance: newRunningBalanceForModified,
        profitLoss: newTrade.profitLoss,
        commission: newTrade.commission,
        swap: newTrade.swap
      });
      
      // 4. Mettre à jour tous les trades suivants (effet cascade)
      tradesToUpdate.forEach(({ id, ref, data }) => {
        const newRunningBalance = (data.runningBalance || 0) + difference;
        batch.update(ref, {
          runningBalance: newRunningBalance
        });
      });
      
      // 5. Mettre à jour la balance actuelle du compte
      const accountRef = doc(db, this.ACCOUNTS_COLLECTION, accountId);
      const accountDoc = await getDoc(accountRef);
      if (accountDoc.exists()) {
        const currentBalance = accountDoc.data().currentBalance || 0;
        batch.update(accountRef, {
          currentBalance: currentBalance + difference,
          updatedAt: new Date()
        });
      }
      
      // 6. Exécuter toutes les mises à jour en une seule transaction
      await batch.commit();
      
      // console.log supprimé
      
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  }

  /**
   * Calcule la running balance pour un nouveau trade
   */
  async calculateRunningBalanceForNewTrade(accountId: string, tradeDate: string, profitLoss: number): Promise<number> {
    const userId = getAuth().currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');
    try {
      // S'assurer que les running balances existent (initialisation automatique)
      await this.ensureRunningBalancesExist(accountId, userId);
      // Récupérer le compte pour la balance initiale
      const accountDoc = await getDoc(doc(db, this.ACCOUNTS_COLLECTION, accountId));
      if (!accountDoc.exists()) {
        throw new Error('Compte non trouvé');
      }
      
      const account = accountDoc.data();
      const initialBalance = account.initialBalance || 0;
      
      // Récupérer tous les trades AVANT ce nouveau trade (strictement avant)
      const tradesQuery = query(
        collection(db, this.TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', userId)
      );
      
      const tradesSnapshot = await getDocs(tradesQuery);
      let runningBalance = initialBalance;
      
      // Filtrer côté client pour éviter les index composites
      const validTrades = tradesSnapshot.docs
        .map(doc => ({ id: doc.id, data: doc.data() as Trade }))
        .filter(({ data }) => data.openDateTime && data.openDateTime < tradeDate)
        .sort((a, b) => {
          const dateA = a.data.openDateTime || '';
          const dateB = b.data.openDateTime || '';
          if (dateA === dateB) {
            return a.id.localeCompare(b.id); // Tri croissant par ID
          }
          return dateA.localeCompare(dateB); // Tri croissant par date
        });
      
      // Calculer la balance cumulative en incluant commission et swap
      validTrades.forEach(({ data: trade }) => {
        const profitLoss = trade.profitLoss || 0;
        const commission = trade.commission || 0;
        const swap = trade.swap || 0;
        const totalImpact = profitLoss + commission + swap;
        runningBalance += totalImpact;
      });
      
      // Ajouter l'impact total du nouveau trade (P&L + commission + swap seront ajoutés lors de la sauvegarde)
      runningBalance += profitLoss;
      
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      return runningBalance;
      
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  }

  /**
   * Recalcule toutes les running balances pour un compte (si corruption)
   * ⚠️ ATTENTION: Opération coûteuse, à utiliser seulement si nécessaire
   */
  async recalculateAllRunningBalances(accountId: string, userId?: string): Promise<void> {
    // console.log supprimé
    const resolvedUserId = userId || getAuth().currentUser?.uid;
    if (!resolvedUserId) throw new Error('User not authenticated');
    
    try {
      // Récupérer le compte
      const accountDoc = await getDoc(doc(db, this.ACCOUNTS_COLLECTION, accountId));
      if (!accountDoc.exists()) {
        throw new Error('Compte non trouvé');
      }
      
      const account = accountDoc.data();
      const initialBalance = account.initialBalance || 0;
      
      // Récupérer TOUS les trades du compte (sans tri pour éviter les index composites)
      const tradesQuery = query(
        collection(db, this.TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', resolvedUserId)
      );
      
      const tradesSnapshot = await getDocs(tradesQuery);
      const batch = writeBatch(db);
      let runningBalance = initialBalance;
      
      // Convertir en array et trier côté client (pas d'index requis)
      const trades = tradesSnapshot.docs.map(doc => ({
        id: doc.id,
        ref: doc.ref,
        data: doc.data() as Trade
      }));
      
      // Trier par openDateTime puis par ID (côté client)
      trades.sort((a, b) => {
        const dateA = a.data.openDateTime || '';
        const dateB = b.data.openDateTime || '';
        if (dateA === dateB) {
          return a.id.localeCompare(b.id); // Tri par ID en cas d'égalité
        }
        return dateA.localeCompare(dateB);
      });
      
      // console.log supprimé
      
      // Recalculer chaque running balance dans l'ordre chronologique
      trades.forEach(({ ref, data: trade }) => {
        // Calculer l'impact total du trade : P&L + Commission + Swap
        const profitLoss = trade.profitLoss || 0;
        const commission = trade.commission || 0;
        const swap = trade.swap || 0;
        const totalImpact = profitLoss + commission + swap;
        
        runningBalance += totalImpact;
        
        // console.log supprimé
        
        batch.update(ref, {
          runningBalance: runningBalance
        });
      });
      
      // Mettre à jour la balance actuelle du compte
      batch.update(doc(db, this.ACCOUNTS_COLLECTION, accountId), {
        currentBalance: runningBalance,
        updatedAt: new Date()
      });
      
      await batch.commit();
      
      // console.log supprimé
      
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  }

  /**
   * Obtient la balance à une date précise (plus rapide avec running balance)
   * @param excludeTradeId - ID du trade à exclure du calcul (pour éviter l'auto-inclusion lors d'édition)
   */
  async getBalanceAtDate(accountId: string, targetDate: string, excludeTradeId?: string): Promise<number> {
    const userId = getAuth().currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');
    try {
      // S'assurer que les running balances existent (initialisation automatique)
      await this.ensureRunningBalancesExist(accountId, userId);
      // Récupérer tous les trades du compte (filtrage côté client)
      const tradesQuery = query(
        collection(db, this.TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', userId)
      );
      
      const tradesSnapshot = await getDocs(tradesQuery);
      
      if (tradesSnapshot.empty) {
        // Aucun trade, retourner la balance initiale
        const accountDoc = await getDoc(doc(db, this.ACCOUNTS_COLLECTION, accountId));
        return accountDoc.exists() ? (accountDoc.data().initialBalance || 0) : 0;
      }
      
      // Filtrer et trier côté client pour trouver le trade le plus récent avant la date cible
      const validTrades = tradesSnapshot.docs
        .map(doc => ({ id: doc.id, data: doc.data() as Trade }))
        .filter(({ id, data }) => {
          // Exclure le trade spécifié (pour éviter l'auto-inclusion lors d'édition)
          if (excludeTradeId && id === excludeTradeId) {
            // console.log supprimé
            return false;
          }
          
          // Filtrer par date : strictement avant la date cible
          return data.openDateTime && data.openDateTime < targetDate;
        })
        .sort((a, b) => {
          const dateA = a.data.openDateTime || '';
          const dateB = b.data.openDateTime || '';
          if (dateA === dateB) {
            return b.id.localeCompare(a.id); // Tri décroissant par ID
          }
          return dateB.localeCompare(dateA); // Tri décroissant par date
        });
      
      if (validTrades.length === 0) {
        // Aucun trade avant cette date, retourner la balance initiale
        const accountDoc = await getDoc(doc(db, this.ACCOUNTS_COLLECTION, accountId));
        return accountDoc.exists() ? (accountDoc.data().initialBalance || 0) : 0;
      }
      
      // Prendre le premier trade (le plus récent avant la date)
      const latestTrade = validTrades[0].data;
      const balance = latestTrade.runningBalance || 0;
      
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      if (excludeTradeId) {
        // console.log supprimé
      }
      
      return balance;
      
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  }
}

export const runningBalanceService = new RunningBalanceService(); 
